import React, { useState } from 'react';
import { MusicIcon, UsersIcon } from 'lucide-react';
import { castCalc } from '../data/content';
import { SectionHeading } from './SectionHeading';

/** New in this landing: pick a class size and see how a play is cast (one dot per child), plus the songs strip */
export function CastCalculator() {
  const [active, setActive] = useState(2);
  const size = castCalc.sizes[active];
  const dots = [...Array.from({
    length: size.narrators
  }, () => 'bg-sun'), ...Array.from({
    length: size.main
  }, () => 'bg-coral'), ...Array.from({
    length: size.groups
  }, () => 'bg-white')];
  const legend = [{
    dot: 'bg-sun',
    value: size.narrators === 0 ? 'YOU' : String(size.narrators),
    label: castCalc.roles.narrators
  }, {
    dot: 'bg-coral',
    value: String(size.main),
    label: castCalc.roles.main
  }, {
    dot: 'bg-white',
    value: String(size.groups),
    label: castCalc.roles.groups
  }];
  return <section aria-labelledby="cast-title" className="bg-ink py-12 md:py-16" data-id="element-56" data-mp="1wh8y7d715coe">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-57" data-mp="246k86rwtwbgl">
        <SectionHeading id="cast-title" tone="dark" eyebrow={castCalc.eyebrow} title={castCalc.title} sub={castCalc.sub} data-id="element-58" data-mp="2496havzdaz0x" />

        <div className="mx-auto mt-8 max-w-[760px] rounded-2xl bg-brand p-4 ring-1 ring-white/10 md:mt-10 md:p-7" data-id="element-59" data-mp="2d7i5krcrhkq7">
          <p className="flex items-center justify-center gap-2 font-display text-[12px] font-black tracking-[0.12em] text-sun" data-id="element-60" data-mp="295n2bhb9sx9s">
            <UsersIcon className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" data-id="element-61" data-mp="1lhdlktswyzpi" />
            {castCalc.sizesLabel.toUpperCase()}
          </p>
          <div role="tablist" aria-label={castCalc.sizesLabel} className="mt-3 grid grid-cols-5 gap-2" data-id="element-62" data-mp="1u8w2j6xopjjn">
            {castCalc.sizes.map((s, i) => {
            const on = i === active;
            return <button key={s.n} role="tab" type="button" aria-selected={on} onClick={() => setActive(i)} className={`min-h-[52px] rounded-xl font-display text-[20px] font-black transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sun ${on ? 'bg-sun text-ink' : 'bg-white/10 text-white hover:bg-white/15'}`} data-id="element-63" data-mp="11scxt8xrwvzq" data-mpi={i}>
                  {s.n}
                </button>;
          })}
          </div>

          <div className="mt-6 flex min-h-[132px] flex-wrap content-start justify-center gap-1.5" aria-hidden="true" data-id="element-64" data-mp="2dyqa9i3dxvhf">
            {dots.map((c, i) => <span key={i} className={`h-5 w-5 rounded-full ${c} shadow-[inset_0_-2px_0_rgba(0,0,0,0.18)]`} data-id="element-65" data-mp="2nutovnffbml9" data-mpi={i} />)}
          </div>

          <dl className="mt-5 grid grid-cols-3 gap-2" data-id="element-66" data-mp="2svhd61c9ppbs">
            {legend.map(l => <div key={l.label} className="rounded-xl bg-white/[0.07] px-2 py-3 text-center" data-id="element-67" data-mp="34ccvv5h5v06">
                <dd className="flex items-center justify-center gap-1.5 font-display text-[22px] font-black leading-none text-white" data-id="element-68" data-mp="82dwch6tbwc6">
                  <span className={`h-3 w-3 rounded-full ${l.dot}`} aria-hidden="true" data-id="element-69" data-mp="1snsqpr46xie1" />
                  {l.value}
                </dd>
                <dt className="mt-1.5 text-[10px] font-bold tracking-[0.1em] text-white/80 sm:text-[11px]" data-id="element-70" data-mp="1iveluphtfvnn">{l.label}</dt>
              </div>)}
          </dl>
          <p className="mt-4 text-center text-[15px] font-semibold leading-snug text-white" data-id="element-71" data-mp="18y0klx4grdfx">{size.note}</p>
          <p className="mt-1 text-center text-[13px] text-white/70" data-id="element-72" data-mp="14wncthht906d">Groups: {castCalc.groupsNames}</p>
        </div>

        <div className="mx-auto mt-6 max-w-[760px] rounded-2xl bg-sun p-5 md:p-7" data-id="element-73" data-mp="zwsnvjhlikny">
          <div className="flex items-center gap-3" data-id="element-74" data-mp="uyhcv5fke445">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ink text-sun" data-id="element-75" data-mp="uzsht8kqxhwu">
              <MusicIcon className="h-5 w-5" strokeWidth={2.5} aria-hidden="true" data-id="element-76" data-mp="2u990d20ziv19" />
            </span>
            <h3 className="font-display text-[18px] font-black tracking-tight text-ink md:text-[20px]" data-id="element-77" data-mp="2pa5tdc1g2eqn">{castCalc.songsTitle}</h3>
          </div>
          <p className="mt-3 text-[15px] leading-relaxed text-ink/85 md:text-[16px]" data-id="element-78" data-mp="1r0xrko37gigv">{castCalc.songsText}</p>
          <ul className="mt-4 flex flex-wrap gap-2" data-id="element-79" data-mp="1d1g3yc1nbb93">
            {castCalc.songs.map(s => <li key={s} className="rounded-full bg-white px-3 py-1.5 text-[13px] font-semibold text-ink" data-id="element-80" data-mp="1hyybpz3xfkfi">
                {s}
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}